import React, { useState, useEffect } from "react";
import { SearchInputComponent } from "../../common/components";
import { EmailLetterFilterProps } from "./email-letter-types";
import { SelectInputComponent } from "../../common/components";
// import importDownArrowIcon from "../../assets/icons/import-down-arrow.svg";
import ImportTemplatesPopup from "./import-templates-popup";
import {
  parseImportedTemplateFile,
  importTemplates,
} from "../../api/email-letter-templates";
import { AlertComponent } from "../../common/components";
import { useResourcePermissions } from "../../common/hooks/resource-permissions";
import MODULES from "../../constants/modules";

const EmailLetterFilters: React.FC<EmailLetterFilterProps> = ({
  onSearch,
  isResetSearch = false,
  searchRef,
  viewAllOptions = [],
  setActiveFilters,
  activeFilters = {},
  templateType = "Email",
  fetchTemplatesList,
  setLoading,
  isShowCreateTemplate,
  setShowCreateTemplateOpen,
}) => {
  const [isImportPopupOpen, setIsImportPopupOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [importedTemplates, setImportedTemplates] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [selectedTemplates, setSelectedTemplates] = React.useState<number[]>(
    []
  );
  const [errorMessage, setErrorMessage] = React.useState("");
  const [activeStep, setActiveStep] = React.useState(0);
  const [trackUploadId, setTrackUploadId] = useState<number | null>(null);
  const moduleName =
    templateType === "Email"
      ? MODULES.EMAIL_TEMPLATES
      : MODULES.LETTER_TEMPLATES;
  const { canRead, canWrite, canDelete } = useResourcePermissions(moduleName);
  const { canImport } = useResourcePermissions(MODULES.IMPORT);

  const handleImportCancel = () => {
    setFile(null);
    setErrorMessage("");
    setActiveStep(0);
    setImportedTemplates([]);
    setSelectedTemplates([]);
    setIsImportPopupOpen(false);
  };

  const handleContinueImport = async () => {
    if (!file) {
      return;
    }
    try {
      setLoading?.(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("templateType", templateType);
      const response = await parseImportedTemplateFile(formData);
      if (response?.data?.success === true) {
        if (
          response?.data?.data &&
          response?.data?.data?.createdTemplatesList.length > 0
        ) {
          setImportedTemplates(response?.data?.data.createdTemplatesList);
          setTrackUploadId(response?.data?.data.trackUploadId);
        } else {
          setAlertMessage("No valid templates found in the file.");
          setAlertType("warning");
          setAlertOpen(true);
        }
      }
    } catch (error: any) {
      console.log('ERROR WHILE PARSING XML FILE:', error);
      setAlertMessage(
        error?.response?.data?.message || "Error parsing XML file"
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading?.(false);
    }
  };

  const handleCompleteImport = async () => {
    if (importedTemplates.length === 0) {
      setAlertMessage(
        "No templates to import. Please select templates to import."
      );
      setAlertType("warning");
      setAlertOpen(true);
      return;
    }
    try {
      setLoading?.(true);
      const response = await importTemplates({
        templateIds: selectedTemplates,
        templateType: templateType,
        trackUploadId: trackUploadId,
      });
      if (response?.data?.success === true) {
        setAlertMessage(
          `${selectedTemplates.length} ${selectedTemplates.length === 1 ? "template" : "templates"} imported successfully.`
        );
        setAlertType("success");
        setAlertOpen(true);
        fetchTemplatesList?.();
        handleImportCancel();
      } else {
        setAlertMessage("Failed to import templates.");
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Error importing templates"
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading?.(false);
    }
  };

  useEffect(() => {
    if (isImportPopupOpen) {
      setFile(null);
      setImportedTemplates([]);
      setSelectedTemplates([]);
      setErrorMessage("");
      setActiveStep(0);
    }
  }, [isImportPopupOpen]);

  const handleAddNewTemplate = () => {
    console.log("handleAddNewTemplate is called");
    setShowCreateTemplateOpen?.(true);
  };

  useEffect(() => {
    if (activeStep === 0) {
      setSelectedTemplates([]);
    }
  }, [activeStep]);

  return (
    <div className="campaign-list-search-filter-wrapper  email-letter-filters">
      <div className="campaign-list-search-wrapper">
        <SearchInputComponent
          placeholder="Search by template name"
          onSearch={onSearch}
          isReset={isResetSearch}
          ref={searchRef}
        />
      </div>
      <div className="campaign-list-filter-wrapper">
        <div className="campaign-list-action-filter">
          <SelectInputComponent
            fieldName="emailLetterFolderSelect"
            value={activeFilters.folderName || "View All"}
            options={viewAllOptions}
            onChange={(e: any) => {}}
            handleOption={(e: any) => {
              setActiveFilters?.({
                folderId: e?.id,
                folderName: e?.value,
              });
            }}
          />
        </div>
        <div>
          <button
            className={`import-btn`}
            onClick={() => setIsImportPopupOpen(true)}
            disabled={!canImport}
          >
            <div className="import-btn-text">
              {"Import"}
              {/* <img
                src={importDownArrowIcon}
                alt="ImportDownArrow Icon"
                className="import-down-arrow-img"
              /> */}
            </div>
          </button>
        </div>
        <div className="custom-btn-wrapper">
          <button
            className="custom-btn create-rule-btn"
            onClick={() => {
              handleAddNewTemplate();
            }}
            disabled={!canWrite}
          >
            Add {templateType} Template
          </button>
        </div>
      </div>
      <ImportTemplatesPopup
        isOpen={isImportPopupOpen}
        onClose={() => {
          handleImportCancel();
        }}
        onCompleteImport={() => {
          handleCompleteImport();
        }}
        onContinueImport={() => {
          handleContinueImport();
        }}
        title={`Import ${templateType} Templates`}
        templateType={templateType}
        file={file}
        setFile={setFile}
        importedTemplates={importedTemplates}
        selectedTemplates={selectedTemplates}
        setSelectedTemplates={setSelectedTemplates}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default EmailLetterFilters;
